import { emptySplitApi as api } from "./emptyApi"
import type {
  CompleteMetaAgencyCredentialOauthRequest,
  CreatePartnershipContentRequest,
  CreatePartnershipRequest,
  CreatePartnershipRowFromAdRequest,
  LinkPartnershipAdRequest,
  MetaAgencyCredentialOauthUrlDto,
  MetaAgencyCredentialStatusDto,
  PartnershipAdDto,
  PartnershipBusinessDetectResultDto,
  PartnershipContentRowDto,
  PartnershipDto,
  PartnershipLaunchPreviewDto,
  PartnershipLaunchRequest,
  PartnershipLaunchResponse,
  PartnershipOverviewDto,
  PartnershipSettingsDto,
  PartnershipSyncTriggerResponse,
  RemoveMetaAgencyCredentialResponse,
  SetMetaAgencyCredentialRequest,
  SetPartnershipSettingsRequest,
  SetPartnershipSettingsResponse,
  UpdatePartnershipContentRequest,
  UpdatePartnershipRequest,
} from "./types/partnerships.types"

/**
 * Partnership ads tracker (MD-5524). One overview query feeds the whole
 * AdBlaster → Partnerships tab; every mutation invalidates the single
 * `Partnership` tag so the overview refetches after any edit.
 */
export const partnershipsApi = api.injectEndpoints({
  endpoints: build => ({
    getPartnershipOverview: build.query<PartnershipOverviewDto, void>({
      query: () => "/partnerships/overview",
      providesTags: ["Partnership"],
    }),

    getPartnershipSettings: build.query<PartnershipSettingsDto, void>({
      query: () => "/partnerships/settings",
      providesTags: ["Partnership"],
    }),

    /**
     * Super-admin only: per-company Business Manager id override (null = use
     * default). The API saves first, then probes the saved id and returns the
     * outcome as `verification` (null when nothing could be probed).
     */
    setPartnershipSettings: build.mutation<
      SetPartnershipSettingsResponse,
      SetPartnershipSettingsRequest
    >({
      query: body => ({
        url: "/partnerships/settings",
        method: "PUT",
        body,
      }),
      invalidatesTags: ["Partnership"],
    }),

    /**
     * Super-admin only, throttled 5/min: probe every Business Manager the
     * connected Meta user could run discovery through and say which ones work
     * for this brand. Read-only on our side — nothing to invalidate.
     */
    detectPartnershipBusiness: build.mutation<
      PartnershipBusinessDetectResultDto,
      void
    >({
      query: () => ({
        url: "/partnerships/settings/detect",
        method: "POST",
      }),
    }),

    /* ----- Agency discovery credential (super admin, MD-5501) ----- */

    /**
     * Status of the single Marathon login content discovery runs on for
     * system-user brands. Never carries the credential value. Shares the
     * `Partnership` tag: setting / removing it changes every brand's
     * `settings.discoveryCredential`, so the overview must refetch too.
     */
    getMetaAgencyCredentialStatus: build.query<
      MetaAgencyCredentialStatusDto,
      void
    >({
      query: () => "/partnerships/agency-credential",
      providesTags: ["Partnership"],
    }),

    /** Throttled 5/min. The API validates the login first and rejects system users. */
    setMetaAgencyCredential: build.mutation<
      MetaAgencyCredentialStatusDto,
      SetMetaAgencyCredentialRequest
    >({
      query: body => ({
        url: "/partnerships/agency-credential",
        method: "PUT",
        body,
      }),
      invalidatesTags: ["Partnership", "MetaAccountUser", "MetaAccess"],
    }),

    removeMetaAgencyCredential: build.mutation<
      RemoveMetaAgencyCredentialResponse,
      void
    >({
      query: () => ({
        url: "/partnerships/agency-credential",
        method: "DELETE",
      }),
      invalidatesTags: ["Partnership", "MetaAccountUser", "MetaAccess"],
    }),

    /**
     * Throttled 10/min. Meta's login dialog URL for the agency credential.
     * Every call mints a fresh single-use `state`, so it is used through the
     * lazy hook right before `window.location.assign` and never kept.
     */
    getMetaAgencyCredentialOauthUrl: build.query<
      MetaAgencyCredentialOauthUrlDto,
      void
    >({
      query: () => "/partnerships/agency-credential/oauth-url",
      keepUnusedDataFor: 0,
    }),

    /**
     * Throttled 5/min. Finishes a login started from Manage → Connect with
     * Facebook: the API exchanges the code, validates the login like a
     * pasted token and stores it. Same response and invalidation as
     * `setMetaAgencyCredential`.
     */
    completeMetaAgencyCredentialOauth: build.mutation<
      MetaAgencyCredentialStatusDto,
      CompleteMetaAgencyCredentialOauthRequest
    >({
      query: body => ({
        url: "/partnerships/agency-credential/oauth",
        method: "POST",
        body,
      }),
      invalidatesTags: ["Partnership", "MetaAccountUser", "MetaAccess"],
    }),

    createPartnership: build.mutation<PartnershipDto, CreatePartnershipRequest>(
      {
        query: body => ({
          url: "/partnerships",
          method: "POST",
          body,
        }),
        invalidatesTags: ["Partnership"],
      },
    ),

    updatePartnership: build.mutation<
      PartnershipDto,
      { id: string; body: UpdatePartnershipRequest }
    >({
      query: ({ id, body }) => ({
        url: `/partnerships/${id}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: ["Partnership"],
    }),

    deletePartnership: build.mutation<{ deleted: true }, string>({
      query: id => ({
        url: `/partnerships/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Partnership"],
    }),

    createPartnershipContent: build.mutation<
      PartnershipContentRowDto,
      CreatePartnershipContentRequest
    >({
      query: body => ({
        url: "/partnerships/content",
        method: "POST",
        body,
      }),
      invalidatesTags: ["Partnership"],
    }),

    updatePartnershipContent: build.mutation<
      PartnershipContentRowDto,
      { id: string; body: UpdatePartnershipContentRequest }
    >({
      query: ({ id, body }) => ({
        url: `/partnerships/content/${id}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: ["Partnership"],
    }),

    /** 409 when the row came from Meta discovery (only MANUAL rows can be deleted). */
    deletePartnershipContent: build.mutation<{ deleted: true }, string>({
      query: id => ({
        url: `/partnerships/content/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Partnership"],
    }),

    linkPartnershipAd: build.mutation<
      PartnershipAdDto,
      { id: string; body: LinkPartnershipAdRequest }
    >({
      query: ({ id, body }) => ({
        url: `/partnerships/ads/${id}/link`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["Partnership"],
    }),

    /** Create a creator + MANUAL content row from an unmatched ad and link it. */
    createPartnershipRowFromAd: build.mutation<
      PartnershipContentRowDto,
      { id: string; body: CreatePartnershipRowFromAdRequest }
    >({
      query: ({ id, body }) => ({
        url: `/partnerships/ads/${id}/create-row`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["Partnership"],
    }),

    /** Enqueue the partnership-sync job (202). Poll the returned job id via jobsApi.getJob. */
    triggerPartnershipSync: build.mutation<
      PartnershipSyncTriggerResponse,
      void
    >({
      query: () => ({
        url: "/partnerships/sync",
        method: "POST",
      }),
    }),

    /* ----- Launch as paused partnership ads (MD-5501) ----- */

    /**
     * Throttled 20/min. Resolves every content row × ad set target (name,
     * landing page, blockers, warnings) and dry-runs it against Meta with
     * `validate_only` — creates nothing, so there is nothing to invalidate.
     */
    previewPartnershipLaunch: build.mutation<
      PartnershipLaunchPreviewDto,
      PartnershipLaunchRequest
    >({
      query: body => ({
        url: "/partnerships/launch/preview",
        method: "POST",
        body,
      }),
    }),

    /**
     * Throttled 10/min. Enqueues the `partnership-ad-create` job (202): one
     * PAUSED ad per target, never activated. Poll `jobRunId` via
     * jobsApi.getJob; the caller refetches the overview when it lands (the
     * Ads column changes only once the job has created the ads).
     */
    launchPartnershipAds: build.mutation<
      PartnershipLaunchResponse,
      PartnershipLaunchRequest
    >({
      query: body => ({
        url: "/partnerships/launch",
        method: "POST",
        body,
      }),
    }),
  }),
})

export const {
  useGetPartnershipOverviewQuery,
  useGetPartnershipSettingsQuery,
  useSetPartnershipSettingsMutation,
  useDetectPartnershipBusinessMutation,
  useGetMetaAgencyCredentialStatusQuery,
  useSetMetaAgencyCredentialMutation,
  useRemoveMetaAgencyCredentialMutation,
  useLazyGetMetaAgencyCredentialOauthUrlQuery,
  useCompleteMetaAgencyCredentialOauthMutation,
  useCreatePartnershipMutation,
  useUpdatePartnershipMutation,
  useDeletePartnershipMutation,
  useCreatePartnershipContentMutation,
  useUpdatePartnershipContentMutation,
  useDeletePartnershipContentMutation,
  useLinkPartnershipAdMutation,
  useCreatePartnershipRowFromAdMutation,
  useTriggerPartnershipSyncMutation,
  usePreviewPartnershipLaunchMutation,
  useLaunchPartnershipAdsMutation,
} = partnershipsApi
